import {
  Component,
  ElementRef,
  inject,
  OnInit,
  AfterViewInit,
  ViewChild,
  OnDestroy,
  Inject,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { CreateObjectiveStep1Component } from './create-project-step-1/create-project-step-1.component';
import { CreateObjectiveStep2Component } from './create-project-step-2/create-project-step-2.component';
import { TagsComponent } from './tags/tags.component';
import { StatusComponent } from './status/status.component';
import { IconService } from '../../services/svg-icon.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FormArray, FormGroup } from '@angular/forms';
import { ProjectService } from '../../services/project.service';
import { ReviewProjectFormComponent } from './review-project-form/review-project-form.component';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationPopUpComponent } from '../../ui-component-library/core/confirmation-pop-up/confirmation-pop-up.component';
import { Router } from '@angular/router';
import { catchError, forkJoin, map, of, switchMap, takeUntil } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { ProjectVM, vm_objective } from '../../models/view-models/vm-projects.model';
import { Subject } from 'rxjs';
import { ViewModelMapperService } from '../../services/view-model.mapper.service';
import { dm_user } from '../../models/data-models/dm_user.model';
import { dm_appComponent } from '../../models/data-models/dm_appComponent.model';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { vm_category } from '../../models/view-models/vm-category.model';
import { vm_tag } from '../../models/view-models/vm-tags.model';
import { ExperimentVM } from '../../models/view-models/vm-experiment.model';
import { dm_tagApplication } from '../../models/data-models/dm_tagApplications.model';
import { dm_objectiveApplication } from '../../models/data-models/dm_objectiveApplications.model';

import { dm_categoryApplication } from '../../models/data-models/dm_categoryApplication.model';
import { ComponentType } from '../../models/core-models/enums';
import { dm_status } from '../../models/data-models/dm_status.model';
import { dm_productProject } from '../../models/data-models/dm_product-projects.model';
import { AuthService } from '../../services/auth.service';
import { CommonServices } from '../../services/common.services';
import { ToasterService } from '../../services/toaster.service';
import { ProductCreationService } from '../../services/product-creation.service';

type ProjectStepComponent =
  | CreateObjectiveStep1Component
  | CreateObjectiveStep2Component
  | TagsComponent
  | StatusComponent;

export interface PopupProjectData {
  projectId?: string;
}
@Component({
  selector: 'app-create-project',
  imports: [
    MatStepperModule,
    CreateObjectiveStep1Component,
    CreateObjectiveStep2Component,
    TagsComponent,
    StatusComponent,
    MatIconModule,
    MatButtonModule,
    ReviewProjectFormComponent,
    CommonModule,
    MatDialogModule,
  ],
  templateUrl: './create-project.component.html',
  styleUrl: './create-project.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: { tabindex: '-1' },
})
export class CreateObjectiveComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();

  showReview: boolean = false;
  title = 'Create Project';
  isEditMode = false;
  projectId: string | null = null;
  originalProjectData: ProjectVM | null = null;
  currentProjectData: ProjectVM | null = null;
  projectForEdit: ProjectVM | null = null;
  user: dm_user[] = [];
  appComponentData: dm_appComponent[] = [];
  defaultState: dm_status | undefined;
  lockedProductIdsInEdit = new Set<string>();

  @ViewChild(MatStepper) stepper!: MatStepper;
  @ViewChild('step1') step1?: CreateObjectiveStep1Component;
  @ViewChild('step2') step2?: CreateObjectiveStep2Component;
  @ViewChild('step3') step3?: TagsComponent;
  @ViewChild('step4') step4?: StatusComponent;

  constructor(
    private projectSrvc: ProjectService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router,
    private apiService: ApiService,
    private authService: AuthService,
    private commonService: CommonServices,
    private vmMapper: ViewModelMapperService,
    private toaster: ToasterService,
    private productCreationService: ProductCreationService,
    public dialogRef: MatDialogRef<CreateObjectiveComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PopupProjectData,
    private elementRef: ElementRef<HTMLElement>
  ) {
    this.projectId = data?.projectId || null;
    this.apiService.dataChangeEvent$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data.dataLoaded) {
        this.loadInitialData();
      }
    });
  }

  focus(): void {
    this.elementRef.nativeElement.focus();
  }

  ngOnInit(): void {
    this.projectId = this.data?.projectId || null;
    this.isEditMode = !!this.projectId;
    this.title = this.isEditMode ? 'Edit Project' : 'Create Project';

    // Clear any existing step styling on initialization
    this.clearAllStepStyling();

    if (this.isEditMode) {
      this.initializeEditMode();
    } else {
      this.loadInitialData();
    }
  }

  ngAfterViewInit(): void {
    if (this.isEditMode) {
      this.notifyChildrenDataReady();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.projectSrvc.clearAll();
    this.projectSrvc.clearProjectForEdit();
  }

  private initializeEditMode(): void {
    this.projectForEdit = this.projectSrvc.getCurrentProject();
    if (!this.projectForEdit) {
      this.projectForEdit = this.projectId ? this.findProjectById(this.projectId) : null;
    }

    if (this.projectForEdit) {
      this.originalProjectData = JSON.parse(JSON.stringify(this.projectForEdit)); // Store original data for change detection
      this.currentProjectData = JSON.parse(JSON.stringify(this.projectForEdit));
      this.populateFormsWithProjectData(this.projectForEdit);
    } else {
      this.router.navigate(['/projects']);
    }
    this.loadInitialData();
  }

  private findProjectById(id: string): ProjectVM | null {
    const projects = this.vmMapper.mapProjectData(
      this.apiService.projects,

      this.apiService.experiments,
      this.apiService.tags,
      this.apiService.tagApplications,
      this.apiService.categories,
      this.apiService.categoryApplications,
      this.apiService.icons,
      this.apiService.users,
      this.apiService.batches ?? [],
      this.apiService.objectives,
      this.apiService.objectiveApplications,
      this.apiService.projectTypes,
      this.apiService.statuses,
      this.apiService.products ?? [],
      this.apiService.productProjects ?? []
    );

    return projects.find((prj) => prj.id_project === id) || null;
  }

  private populateFormsWithProjectData(project: ProjectVM): void {
    const lockedProductIds = this.getLinkedExperimentProductIds(project);
    this.lockedProductIdsInEdit = new Set(lockedProductIds);

    // Map and populate Step 1
    const step1Data = {
      projectType: project.project_type || null,
      categories: (project.project_categories || []).map((cat) => ({
        ...cat,
        selected: true,
      })),
      products: (((project as any).project_products || []) as any[]).map((p: any) => ({
        ...p,
        selected: true,
      })),
      lockedProductIds,
    };
    this.projectSrvc.autoSave('step1', step1Data);

    // Map and populate Step 2
    const step2Data = {
      purpose: project.project_title || '',
      description: project.project_description || '',
      startDate: project.project_start_date ? new Date(project.project_start_date) : null,
      endDate: project.project_end_date ? new Date(project.project_end_date) : null,
      projectCostTarget: project.project_cost_target || null,
    };
    this.projectSrvc.autoSave('step2', step2Data);

    // Map and populate Step 3 (Tags)
    const step3Data = {
      tags: (project.project_tags || []).map((tag) => ({
        ...tag,
        selected: true,
      })),
      objectives: (project.project_objectives || []).map((obj) => ({
        ...obj,
        selected: true,
      })),
    };
    this.projectSrvc.autoSave('tags', step3Data);

    // Map and populate Step 4 (Status)
    const step4Data = {
      projectStatus: project.project_status || null,
      projectIcon: project.icon || null,
      state: project.project_status?.name || 'Active',
    };
    this.projectSrvc.autoSave('status', step4Data);
  }

  private notifyChildrenDataReady(): void {
    if (this.step1) this.step1.loadDataForEdit();
    if (this.step2) this.step2.loadDataForEdit();
    if (this.step3) this.step3.loadDataForEdit();
    if (this.step4) this.step4.loadDataForEdit();
  }

  loadInitialData() {
    this.appComponentData = this.apiService.appComponent;
    const projectComponentId =
      this.apiService.appComponent.find(
        (comp) => comp?.appComponentTitle?.toLowerCase() === ComponentType[ComponentType.Projects].toLowerCase()
      )?.id ?? '';
    const pStatus = this.vmMapper.mapAppStatusToComponent(projectComponentId, this.apiService.statuses);
    this.defaultState = pStatus.find((ps) => ps.appStatusTitle?.toLowerCase() === 'new');
  }

  onStepSelectionChange(event: StepperSelectionEvent): void {
    const previousStepIndex = event.previouslySelectedIndex;
    const newStepIndex = event.selectedIndex;

    this.clearAllStepStyling();

    // Save the previous step's data before validation
    if (previousStepIndex >= 0) {
      this.validateAndSaveStep(previousStepIndex);
    }

    // Validate all previous steps up to the new step
    this.validateAllPreviousSteps(newStepIndex);

    console.log(`✅ Navigation completed to step ${newStepIndex + 1}`);
  }

  getNextButtonLabel(): string {
    if (!this.stepper) return 'Next';
    return this.stepper.selectedIndex === this.stepper._steps.length - 1 ? 'Finish' : 'Next';
  }

  onNextOrFinishClick() {
    if (!this.stepper) return;
    if (this.stepper.selectedIndex === this.stepper._steps.length - 1) {
      this.onFinishClick();
    } else {
      this.saveCurrentStepAndNavigate(this.stepper);
    }
  }

  saveCurrentStepAndNavigate(stepper: MatStepper) {
    const currentStepIndex = this.stepper.selectedIndex;
    console.log(`➡️ Next button clicked from step ${currentStepIndex + 1}`);

    this.clearAllStepStyling();

    // Validate and save current step
    const isValid = this.validateAndSaveStep(currentStepIndex);

    if (!isValid) {
      console.log(`Navigation blocked: Step ${currentStepIndex + 1} has validation errors`);
      // this.snackBar.open(
      //   'Please fix the errors in this step before proceeding',
      //   'Close',
      //   { duration: 3000, panelClass: 'toast-warning' }
      // );
      return; // Block navigation here
    }
    // END OF VALIDATION GATE

    stepper.next();
    console.log(`Next navigation completed to step ${stepper.selectedIndex + 1}`);
  }

  private validateAndSaveStep(stepIndex: number): boolean {
    const form = this.getFormForStep(stepIndex);
    let isValid = true;

    if (form) {
      if (form.invalid) {
        this.markFormGroupTouched(form);
        console.log(`❌ Step ${stepIndex + 1} (${this.getStepName(stepIndex)}) has validation errors displayed.`);
        isValid = false;
      } else {
        console.log(`✅ Step ${stepIndex + 1} (${this.getStepName(stepIndex)}) is valid.`);
      }
      this.saveStepData(stepIndex, form);
    }

    return isValid;
  }

  private validateAllPreviousSteps(targetStepIndex: number): void {
    console.log(`🔍 Validating steps 0 to ${targetStepIndex - 1} before step ${targetStepIndex + 1}`);

    for (let i = 0; i < targetStepIndex; i++) {
      this.validateAndSaveStep(i);
    }
  }

  onFinishClick() {
    console.log(`🎯 Finish button clicked - starting comprehensive save and validation`);

    // FIRST: Force save all steps data before any validation
    this.forcesSaveAllStepsData();

    // SECOND: Validate all steps and apply visual feedback (tick/cross icons)
    const validationResult = this.validateAllSteps();

    // THIRD: Update visual state of all steps to show tick/cross icons
    this.updateAllStepsVisualState();

    if (validationResult.isValid) {
      console.log(`All validation passed, proceeding to review`);
      this.showReview = true;
    } else {
      console.log(`Validation failed for steps: ${validationResult.invalidSteps.join(', ')}`);

      const invalidStepsText = validationResult.invalidSteps.join(', ');
      this.snackBar.open(`Please fix the following steps before finishing: ${invalidStepsText}`, 'Close', {
        duration: 5000,
        panelClass: 'toast-error',
      });

      // Navigate to the first invalid step
      this.stepper.selectedIndex = validationResult.firstInvalidStepIndex;
    }
  }

  private forcesSaveAllStepsData(): void {
    try {
      for (let i = 0; i < 4; i++) {
        const form = this.getFormForStep(i);
        if (form) {
          this.saveStepData(i, form);
        } else {
          console.warn(`❌ No form found for step ${i + 1}`);
        }
      }
    } catch (error) {
      console.error(`Error during force save:`, error);
    }
  }

  private validateAllSteps(): { isValid: boolean; invalidSteps: string[]; firstInvalidStepIndex: number } {
    const stepNames = ['Intro', 'Definition', 'Tags', 'Status'];
    const invalidSteps: string[] = [];
    let firstInvalidStepIndex = -1;

    // Validate all form steps
    [0, 1, 2, 3].forEach((stepIndex) => {
      const form = this.getFormForStep(stepIndex);
      if (form && !form.valid) {
        invalidSteps.push(stepNames[stepIndex]);
        if (firstInvalidStepIndex === -1) firstInvalidStepIndex = stepIndex;
        this.markFormGroupTouched(form);
      }
    });

    return {
      isValid: invalidSteps.length === 0,
      invalidSteps,
      firstInvalidStepIndex: firstInvalidStepIndex === -1 ? 0 : firstInvalidStepIndex,
    };
  }

  private updateAllStepsVisualState(): void {
    this.clearAllStepStyling();

    for (let i = 0; i < 4; i++) {
      const form = this.getFormForStep(i);

      if (form) {
        if (form.valid) {
          this.markStepAsValid(i);
        } else {
          this.markStepAsInvalid(i);
        }
      }
    }
  }

  private markStepAsInvalid(stepIndex: number): void {
    setTimeout(() => {
      const stepHeaders = document.querySelectorAll('.mat-step-header');
      if (stepHeaders[stepIndex]) {
        stepHeaders[stepIndex].classList.add('invalid-step');
      }
    }, 100);
  }

  private markStepAsValid(stepIndex: number): void {
    setTimeout(() => {
      const stepHeaders = document.querySelectorAll('.mat-step-header');
      if (stepHeaders[stepIndex]) {
        stepHeaders[stepIndex].classList.remove('invalid-step');
        stepHeaders[stepIndex].classList.add('valid-step');
      }
    }, 100);
  }

  private clearValidStepStyling(): void {
    setTimeout(() => {
      const stepHeaders = document.querySelectorAll('.mat-step-header.valid-step');
      stepHeaders.forEach((header) => header.classList.remove('valid-step'));
    }, 50);
  }

  private clearInvalidStepStyling(): void {
    setTimeout(() => {
      const stepHeaders = document.querySelectorAll('.mat-step-header.invalid-step');
      stepHeaders.forEach((header) => header.classList.remove('invalid-step'));
    }, 50);
  }

  private clearAllStepStyling(): void {
    this.clearInvalidStepStyling();
    this.clearValidStepStyling();
  }

  private saveStepData(stepIndex: number, form: FormGroup) {
    let dataToSave = form.value;
    let stepName: 'step1' | 'step2' | 'tags' | 'status';
    let childComponent: ProjectStepComponent | undefined = undefined;

    switch (stepIndex) {
      case 0:
        stepName = 'step1';
        childComponent = this.step1;
        break;
      case 1:
        stepName = 'step2';
        childComponent = this.step2;
        break;
      case 2:
        stepName = 'tags';
        childComponent = this.step3;
        break;
      case 3:
        stepName = 'status';
        childComponent = this.step4;
        break;
      default:
        return;
    }

    // Try to use getFullData if available, else use form.value
    if (childComponent && typeof childComponent.getFullData === 'function') {
      try {
        dataToSave = childComponent.getFullData();
      } catch (error) {
        dataToSave = form.value;
      }
    } else {
      dataToSave = form.value;
    }

    this.projectSrvc.autoSave(stepName, dataToSave);
    this.updateCurrentProjectData(stepName, dataToSave);
  }

  private getFormForStep(stepIndex: number): FormGroup | undefined {
    switch (stepIndex) {
      case 0:
        return this.step1?.step1Form;
      case 1:
        return this.step2?.step2Form;
      case 2:
        return this.step3?.tagsForm;
      case 3:
        return this.step4?.statusForm;
      default:
        return undefined;
    }
  }

  private getStepName(stepIndex: number): string {
    const stepNames = ['Intro', 'Definition', 'Tags', 'Status'];
    return stepNames[stepIndex];
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      control?.markAsDirty();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }

      if (control instanceof FormArray) {
        control.controls.forEach((arrayControl) => {
          arrayControl.markAsTouched();
          arrayControl.markAsDirty();
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          }
        });
      }
    });

    formGroup.updateValueAndValidity();
  }

  private updateCurrentProjectData(step: string, data: any): void {
    if (!this.currentProjectData) {
      this.currentProjectData = new ProjectVM();
    }

    switch (step) {
      case 'step1':
        this.currentProjectData.project_type = data.projectType;
        this.currentProjectData.project_start_date = data.started;
        this.currentProjectData.project_categories = (data.categories || []).filter((cat: vm_category) => cat.selected);
        (this.currentProjectData as any).project_products = (data.products || []).filter((p: any) => p?.selected);
        break;
      case 'step2':
        this.currentProjectData.project_title = data.purpose || '';
        this.currentProjectData.project_description = data.description || data.details;
        this.currentProjectData.project_start_date = data.startDate;
        this.currentProjectData.project_end_date = data.endDate;
        this.currentProjectData.project_cost_target = data.projectCostTarget;
        break;
      case 'tags':
        this.currentProjectData.project_tags = (data.tags || []).filter((tag: vm_tag) => tag.selected);
        this.currentProjectData.project_objectives = (data.objectives || []).filter(
          (obj: vm_objective) => obj.selected
        );
        break;
      case 'status':
        this.currentProjectData.project_status = data.projectStatus;
        this.currentProjectData.icon = data.projectIcon;
        this.currentProjectData.iconId = data.projectIcon?.id;
        break;
    }
  }

  private detectChanges(): any {
    if (!this.isEditMode || !this.originalProjectData || !this.currentProjectData) {
      return { hasChanges: false, mainProject: false };
    }

    const original = this.originalProjectData;
    const current = this.currentProjectData;

    const mainProject =
      original.project_title !== current.project_title ||
      original.project_description !== current.project_description ||
      original.project_start_date !== current.project_start_date ||
      original.project_end_date !== current.project_end_date ||
      original.project_status?.id !== current.project_status?.id ||
      original.iconId !== current.iconId ||
      original.project_type?.id !== current.project_type?.id ||
      original.project_cost_target !== current.project_cost_target;

    const categoryChanges = this.detectGenericChanges(
      original.project_categories || [],
      current.project_categories || [],
      { idFields: ['id', 'id_category'], itemName: 'categories' }
    );

    const tagChanges = this.detectGenericChanges(original.project_tags || [], current.project_tags || [], {
      idFields: ['id', 'id_tag'],
      itemName: 'tags',
    });

    const objectiveChanges = this.detectGenericChanges(
      original.project_objectives || [],
      current.project_objectives || [],
      { idFields: ['id', 'id_objective'], itemName: 'objectives' }
    );

    const productChanges = this.detectGenericChanges(
      (original as any).project_products || [],
      (current as any).project_products || [],
      { idFields: ['id', 'productId', 'id_product'], itemName: 'products' }
    );

    return {
      hasChanges:
        mainProject ||
        categoryChanges.hasChanges ||
        tagChanges.hasChanges ||
        objectiveChanges.hasChanges ||
        productChanges.hasChanges,
      mainProject,
      categories: categoryChanges,
      tags: tagChanges,
      objectives: objectiveChanges,
      products: productChanges,
    };
  }

  private detectGenericChanges<T>(
    originalItems: T[],
    currentItems: T[],
    config: {
      idFields: string[]; // ['id', 'id_category'] for fallback
      itemName: string; // for logging
    }
  ): {
    hasChanges: boolean;
    toAdd: string[];
    toRemove: string[];
    originalIds: string[];
    currentIds: string[];
    currentItems: T[];
  } {
    const getItemId = (item: any): string | null => {
      for (const field of config.idFields) {
        if (item[field]) return item[field];
      }
      return null;
    };

    const originalIds = (originalItems || [])
      .map(getItemId)
      .filter((id): id is string => !!id)
      .sort();

    const currentIds = (currentItems || [])
      .map(getItemId)
      .filter((id): id is string => !!id)
      .sort();

    const toAdd = currentIds.filter((id) => !originalIds.includes(id));
    const toRemove = originalIds.filter((id) => !currentIds.includes(id));

    console.log(`🔍 ${config.itemName} changes: +${toAdd.length} -${toRemove.length}`);

    return {
      hasChanges: toAdd.length > 0 || toRemove.length > 0,
      toAdd,
      toRemove,
      originalIds,
      currentIds,
      currentItems: currentItems || [],
    };
  }

  private buildUpdateOperations(projectId: string, formData: any, changes: any): any[] {
    const operations: any[] = [];

    // Use generic operation builder for each type
    if (changes.categories.hasChanges) {
      const categoryOps = this.buildGenericOperations(projectId, changes.categories, {
        endpoint: 'categoryApplications',
        findExistingFn: (id: string) =>
          this.apiService.categoryApplications.find(
            (c: dm_categoryApplication) => (c.categoryId || c.id) === id && c.componentId === projectId
          ),
        buildRemovePayloadFn: (existing: dm_categoryApplication) => existing.id,
        buildAddPayloadFn: (item: dm_categoryApplication, projectId: string) => ({
          appComponentTitle: 'Projects',
          categoryId: item.id,
          componentId: projectId,
        }),
      });
      operations.push(...categoryOps);
    }

    if (changes.tags.hasChanges) {
      const tagOps = this.buildGenericOperations(projectId, changes.tags, {
        endpoint: 'tagApplications',
        findExistingFn: (id: string) =>
          this.apiService.tagApplications.find((t: dm_tagApplication) => t.tagId === id && t.componentId === projectId),
        buildRemovePayloadFn: (existing: dm_tagApplication) => existing.id,
        buildAddPayloadFn: (item: dm_tagApplication, projectId: string) => ({
          appComponentTitle: 'Projects',
          tagId: item.id,
          componentId: projectId,
        }),
      });
      operations.push(...tagOps);
    }

    if (changes.objectives.hasChanges) {
      const objectiveOps = this.buildGenericOperations(projectId, changes.objectives, {
        endpoint: 'objectiveApplications',
        findExistingFn: (id: string) =>
          this.apiService.objectiveApplications.find(
            (o: dm_objectiveApplication) => (o.objectiveId || o.id) === id && o.componentId === projectId
          ),
        buildRemovePayloadFn: (existing: dm_objectiveApplication) => existing.id,
        buildAddPayloadFn: (item: dm_objectiveApplication, projectId: string) => ({
          appComponentTitle: 'Projects',
          objectiveId: item.id,
          componentId: projectId,
        }),
      });
      operations.push(...objectiveOps);
    }

    if (changes.products?.hasChanges) {
      const safeProductChanges = {
        ...changes.products,
        toRemove: (changes.products.toRemove || []).filter(
          (id: string) => !this.lockedProductIdsInEdit.has(String(id))
        ),
      };
      safeProductChanges.hasChanges =
        (safeProductChanges.toAdd?.length || 0) > 0 || (safeProductChanges.toRemove?.length || 0) > 0;

      if (safeProductChanges.hasChanges) {
        const productOps = this.buildGenericOperations(projectId, safeProductChanges, {
          endpoint: 'productProjects',
          findExistingFn: (id: string) =>
            (this.apiService.productProjects || []).find(
              (pp: dm_productProject) => pp.productId === id && pp.projectId === projectId
            ),
          buildRemovePayloadFn: (existing: dm_productProject) => existing.id,
          buildAddPayloadFn: (item: any, pid: string) => ({
            productId: item.id || item.productId,
            projectId: pid,
          }),
        });
        operations.push(...productOps);
      }
    }

    return operations;
  }

  /**
   * 🔄 CONSOLIDATED METHOD: Generic API operation builder
   */
  private buildGenericOperations(
    projectId: string,
    changes: any,
    config: {
      endpoint: string;
      findExistingFn: (id: string) => any;
      buildRemovePayloadFn?: (existing: any) => any;
      buildAddPayloadFn: (item: any, projectId: string) => any;
    }
  ): any[] {
    const operations: any[] = [];

    // Remove operations
    changes.toRemove.forEach((id: string) => {
      const existing = config.findExistingFn(id);
      if (existing) {
        const removeId = config.buildRemovePayloadFn ? config.buildRemovePayloadFn(existing) : existing.id;

        operations.push(
          this.apiService.delete(config.endpoint, removeId).pipe(
            catchError((error) => {
              console.error(`Error removing ${config.endpoint} ${id}:`, error);
              return of(null);
            })
          )
        );
      }
    });

    // Add operations
    changes.toAdd.forEach((id: string) => {
      const item = changes.currentItems.find((item: any) => {
        return (
          item.id === id ||
          item.productId === id ||
          item.id_category === id ||
          item.id_tag === id ||
          item.id_objective === id ||
          item.id_experiment === id
        );
      });

      if (item) {
        const payload = config.buildAddPayloadFn(item, projectId);
        operations.push(
          this.apiService.create(config.endpoint, payload).pipe(
            catchError((error) => {
              console.error(`Error adding ${config.endpoint} ${id}:`, error);
              return of(null);
            })
          )
        );
      }
    });

    return operations;
  }

  private buildProjectPayload(formData: any): any {
    const step1Data = formData.step1 || {};
    const step2Data = formData.step2 || {};
    const statusData = formData.status || {};
    const tenantId = this.authService.getTenantId();
    const projectStartDate = step2Data.startDate ? new Date(step2Data.startDate) : null;

    if (projectStartDate) {
      projectStartDate.setHours(0, 0, 0, 0);
    }

    const payload = {
      projectTypeId: step1Data.projectType?.id || null,
      projectTitle: step2Data.purpose || '',
      tenantId: tenantId,
      iconId: statusData.projectIcon?.id || null,
      appStatusId: statusData.projectStatus?.id || null,
      projectStartDate: projectStartDate ? this.commonService.normalizeDate(projectStartDate) : null,
      projectEndDate: step2Data.endDate ? this.commonService.normalizeDate(step2Data.endDate) : null,
      projectDescription: step2Data.description || step2Data.details || '',
      projectCostTarget: step2Data.projectCostTarget || '',
      version: this.projectForEdit?.version,
    };

    return payload;
  }

  private handleSaveSuccess(action: 'created' | 'updated' | 'no_changes', projectId?: string | null) {
    let message = '';

    switch (action) {
      case 'created':
        message = 'Project created successfully';
        break;
      case 'updated':
        message = 'Project updated successfully';
        break;
      case 'no_changes':
        message = 'No changes detected';
        break;
    }
    this.projectSrvc.clearAll();
    this.projectSrvc.clearProjectForEdit();
    // this.dialog?.closeAll();
    this.dialogRef.close({ success: true, message: message, projectId: projectId ?? undefined });
  }

  onCancelClick() {
    this.syncCurrentProjectDataFromForms();
    let shouldShowConfirmation = false;

    if (this.isEditMode) {
      // In edit mode, show confirmation only if there are changes
      const changes = this.detectChanges();
      shouldShowConfirmation = changes.hasChanges;
    } else {
      shouldShowConfirmation = this.isAnyValueEntered();
    }

    if (shouldShowConfirmation) {
      const dialogRef2 = this.dialog.open(ConfirmationPopUpComponent, {
        data: {
          title: this.isEditMode ? 'Cancel Edit' : 'Cancel Project',
          message: 'Are you sure you want to cancel? All changes will be lost.',
          confirmText: 'Confirm',
          cancelText: 'Cancel',
          cssClass: 'confirmation-dialog',
        },
        autoFocus: false,
        disableClose: true,
      });

      dialogRef2.afterClosed().subscribe((result) => {
        if (result) {
          this.projectSrvc.clearAll();
          this.projectSrvc.clearProjectForEdit();
          this.dialog.closeAll();
        }
      });
    } else {
      // No changes/touched, close directly
      this.projectSrvc.clearAll();
      this.projectSrvc.clearProjectForEdit();
      this.dialog.closeAll();
    }
  }

  reviewCancel() {
    this.clearAllStepStyling();
    this.showReview = false;
    this.stepper.selectedIndex = this.stepper.steps.length - 1;
  }

  saveData() {
    if (this.isEditMode) {
      this.updateProject();
    } else {
      this.createProject();
    }
  }

  private createProject() {
    const formData = this.projectSrvc.projectFormData;
    const projectPayload = this.buildProjectPayload(formData);

    this.apiService
      .create('projects', projectPayload)
      .pipe(
        switchMap((projectResponse: any) => {
          const projectId = projectResponse.body[0].id;

          if (!projectId) {
            throw new Error('Project ID not returned from server');
          }

          return this.saveRelatedData(projectId, formData).pipe(map((results) => ({ projectId, results })));
        }),
        catchError((error) => {
          console.error('Error in save process:', error);
          this.snackBar.open('An unexpected error occurred. Please try again.', 'Close', {
            duration: 6000,
            panelClass: ['toast-error'],
          });
          return of(null);
        })
      )
      .subscribe({
        next: (result) => {
          if (result) {
            this.handleSaveSuccess('created', result.projectId);
          }
        },
        error: (error) => {
          console.log(error);
        },
      });
  }

  private updateProject() {
    const formData = this.projectSrvc.projectFormData;
    const changes = this.detectChanges();

    if (!changes.hasChanges) {
      this.handleSaveSuccess('no_changes', this.projectId);
      return;
    }

    const updateOperations: any[] = [];

    // Update main project
    if (changes.mainProject) {
      const projectPayload = this.buildProjectPayload(formData);
      projectPayload.id = this.projectId;
      updateOperations.push(this.apiService.update('projects', projectPayload));
    }

    // Add granular update operations
    const granularOps = this.buildUpdateOperations(this.projectId!, formData, changes);
    updateOperations.push(...granularOps);

    if (updateOperations.length === 0) {
      this.handleSaveSuccess('no_changes', this.projectId);
      return;
    }

    // Execute all operations
    forkJoin(updateOperations)
      .pipe(
        catchError((error) => {
          console.error('Error updating project:', error);
          return of(null);
        })
      )
      .subscribe({
        next: (results) => {
          this.handleSaveSuccess('updated', this.projectId);
        },
        error: (error) => {
          console.error('Update failed:', error);
        },
      });
  }

  private saveRelatedData(projectId: string, formData: any) {
    const saveOperations: any[] = [];

    // Icon control
    const iconControlSave = this.saveGenericApplications(projectId, [formData.status], {
      endpoint: 'iconControls',
      payloadBuilder: (statusData: any, projectId: string) => ({
        appComponentId: projectId,
        iconId: statusData.projectIcon?.id,
      }),
      condition: (statusData: any) => !!statusData.projectIcon?.id,
    });
    saveOperations.push(...iconControlSave);

    // Categories
    const categorySaves = this.saveGenericApplications(
      projectId,
      (formData.step1?.categories || []).filter((cat: vm_category) => cat?.selected),
      {
        endpoint: 'categoryApplications',
        payloadBuilder: (category: vm_category, projectId: string) => ({
          appComponentTitle: 'Projects',
          categoryId: category.id,
          componentId: projectId,
        }),
      }
    );
    saveOperations.push(...categorySaves);

    // Objectives
    const objectiveSaves = this.saveGenericApplications(
      projectId,
      (formData.tags?.objectives || []).filter((obj: any) => obj?.selected),
      {
        endpoint: 'objectiveApplications',
        payloadBuilder: (objective: any, projectId: string) => ({
          appComponentTitle: 'Projects',
          objectiveId: objective.id || objective.id_objective,
          componentId: projectId,
        }),
      }
    );
    saveOperations.push(...objectiveSaves);

    // Tags
    const tagSaves = this.saveGenericApplications(
      projectId,
      (formData.tags?.tags || []).filter((tag: any) => tag?.selected),
      {
        endpoint: 'tagApplications',
        payloadBuilder: (tag: any, projectId: string) => ({
          appComponentTitle: 'Projects',
          tagId: tag.id || tag.id_tag,
          componentId: projectId,
        }),
      }
    );
    saveOperations.push(...tagSaves);

    const selectedProductIds = this.getSelectedProductIdsFromStep1(formData.step1);
    const productProjectSaves = this.saveGenericApplications(
      projectId,
      selectedProductIds.map((id) => ({ id })),
      {
        endpoint: 'productProjects',
        payloadBuilder: (item: { id: string }, pid: string): Partial<dm_productProject> => ({
          productId: item.id,
          projectId: pid,
        }),
        condition: (item: { id: string }) => !!item?.id,
      }
    );
    saveOperations.push(...productProjectSaves);

    return forkJoin(saveOperations).pipe(
      map((results) => {
        const successful = results.filter((r) => r.success);
        const failed = results.filter((r) => !r.success);

        console.log(`Save completed: ${successful.length} successful, ${failed.length} failed`);

        if (failed.length > 0) {
          console.warn('Some operations failed:', failed);
        }

        return {
          successful,
          failed,
          hasFailures: failed.length > 0,
          allSuccessful: failed.length === 0,
        };
      })
    );
  }

  private saveGenericApplications(
    projectId: string,
    items: any[],
    config: {
      endpoint: string;
      payloadBuilder: (item: any, projectId: string) => any;
      condition?: (item: any) => boolean;
    }
  ): any[] {
    const filteredItems = config.condition ? items.filter(config.condition) : items;

    return filteredItems.map((item: any) => {
      const payload = config.payloadBuilder(item, projectId);
      return this.apiService.create(config.endpoint, payload).pipe(
        catchError((error) => {
          console.error(`Failed to save ${config.endpoint}:`, error);
          return of({ success: false, error, type: config.endpoint });
        }),
        map((result) => ({ success: true, data: result, type: config.endpoint }))
      );
    });
  }

  // Methods to check if data is changed in EDIT Mode(Helps in deciding to show the confirmation modal)
  private syncCurrentProjectDataFromForms(): void {
    // Step 1
    if (this.step1?.getFullData) {
      this.updateCurrentProjectData('step1', this.step1.getFullData());
    } else if (this.step1?.step1Form) {
      this.updateCurrentProjectData('step1', this.step1.step1Form.value);
    }
    // Step 2
    if (this.step2?.getFullData) {
      this.updateCurrentProjectData('step2', this.step2.getFullData());
    } else if (this.step2?.step2Form) {
      this.updateCurrentProjectData('step2', this.step2.step2Form.value);
    }
    // Step 3
    if (this.step3?.getFullData) {
      this.updateCurrentProjectData('tags', this.step3.getFullData());
    } else if (this.step3?.tagsForm) {
      this.updateCurrentProjectData('tags', this.step3.tagsForm.value);
    }
    // Step 4
    if (this.step4?.getFullData) {
      this.updateCurrentProjectData('status', this.step4.getFullData());
    } else if (this.step4?.statusForm) {
      this.updateCurrentProjectData('status', this.step4.statusForm.value);
    }
  }

  // Methods to check if data is entered in CREATE Mode(Helps in deciding to show the confirmation modal)
  private isAnyValueEntered(): boolean {
    // Step 1
    const step1 = this.step1?.step1Form?.value;
    if (step1) {
      if (step1.projectType) return true;
      if (Array.isArray(step1.categories) && step1.categories.some((cat: any) => cat)) return true;
      if (Array.isArray(step1.products) && step1.products.some((p: any) => !!p)) return true;
    }
    // Step 2
    const step2 = this.step2?.step2Form?.value;
    if (step2) {
      if (step2.purpose) return true;
      if (step2.description) return true;
      if (step2.startDate && !this.isDefaultToday(step2.startDate)) return true;
      if (step2.endDate) return true;
    }
    // Step 3 (Tags)
    const step3 = this.step3?.tagsForm?.value;
    if (step3) {
      if (Array.isArray(step3.tags) && step3.tags.some((tag: any) => !!tag)) return true;
      if (Array.isArray(step3.objectives) && step3.objectives.some((obj: any) => !!obj)) return true;
    }
    // Step 4 (Status)
    const step4 = this.step4?.statusForm?.value;
    if (step4) {
      if (step4.projectStatus && step4.projectStatus !== this.defaultState?.id) return true;
      if (step4.projectIcon) return true;
    }
    return false;
  }

  isDefaultToday(dateValue: any): boolean {
    const today = new Date();
    let date: Date;
    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else {
      return false;
    }
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  }

  private getSelectedProductIdsFromStep1(step1Data: any): string[] {
    if (!step1Data) return [];

    const productsData = step1Data.products;

    // getFullData() shape: [{ id, ..., selected }]
    if (Array.isArray(productsData) && productsData.length > 0 && typeof productsData[0] === 'object') {
      return productsData.filter((p: any) => !!p?.selected && !!p?.id).map((p: any) => String(p.id));
    }

    // fallback shape: boolean[] from raw form
    if (Array.isArray(productsData) && productsData.length > 0 && typeof productsData[0] === 'boolean') {
      const sourceProducts = this.apiService.products ?? [];
      return productsData
        .map((selected: boolean, idx: number) => (selected ? sourceProducts[idx]?.id : null))
        .filter((id: string | null): id is string => !!id)
        .map((id) => String(id));
    }

    return [];
  }

  private getLinkedExperimentProductIds(project: ProjectVM): string[] {
    const ids = new Set<string>();

    for (const exp of project?.project_experiments || []) {
      const single = (exp as any)?.experimentProduct;
      const singleId = String(single?.id ?? single?.productId ?? '').trim();
      if (singleId) ids.add(singleId);

      const list =
        (exp as any)?.experimentProducts ?? (exp as any)?.experiment_products ?? (exp as any)?.products ?? [];

      for (const p of list) {
        const id = String(p?.id ?? p?.productId ?? '').trim();
        if (id) ids.add(id);
      }
    }

    return Array.from(ids);
  }
  onCreateTestProduct(): void {
    this.productCreationService
      .createProduct('Create Test Product')
      .pipe(takeUntil(this.destroy$))
      .subscribe((createdProduct) => {
        if (createdProduct) {
          this.step1?.refreshProducts();
        }
      });
  }
}
